import { useId } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  TREE_MEMBER_ROLES,
  type TreeMemberRole,
} from '@/services/waterTreeMemberRoles'

type Props = {
  value: TreeMemberRole
  onChange: (role: TreeMemberRole) => void
  disabled?: boolean
}

export default function MemberRolePicker({
  value,
  onChange,
  disabled = false,
}: Props) {
  const labelId = useId()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const textColor = isDarkMode ? '#BBD7FF' : '#003F61'
  const borderColor = isDarkMode ? '#43597F' : '#EAEDF2'

  return (
    <Box
      dir="rtl"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}
    >
      <Typography
        id={labelId}
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: textColor,
          fontFamily: 'Rubik',
          whiteSpace: 'nowrap',
        }}
      >
        תפקיד
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        disabled={disabled}
        aria-labelledby={labelId}
        onChange={(_, next: TreeMemberRole | null) => {
          // Clicking the pressed button reports null; a member always has a role.
          if (next) onChange(next)
        }}
        sx={{
          gap: 1,
          // The group's joined corners assume a left-to-right row and come out
          // reversed in this right-to-left one, so each button keeps its own.
          '&& .MuiToggleButtonGroup-grouped': {
            m: 0,
            border: `1px solid ${borderColor}`,
            borderRadius: '6px',
          },
        }}
      >
        {TREE_MEMBER_ROLES.map(role => (
          <ToggleButton
            key={role.key}
            value={role.key}
            sx={{
              px: 2,
              height: 32,
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Rubik',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              color: textColor,
              backgroundColor: isDarkMode ? '#091327' : '#FFFFFF',
              '&:hover': {
                backgroundColor: isDarkMode ? '#1D325A' : '#F1F8FE',
              },
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: '#004A7C',
                color: '#FFFFFF',
              },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          >
            {role.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
}
